import { useEffect, useRef, useState } from 'react';
import Intercom, { shutdown } from '@intercom/messenger-js-sdk';
import { configService } from '@/services/configService';
import { intercomService } from '@/services/intercom-service';
import type { IntercomUser } from '@/types/intercom';

interface UseIntercomOptions {
  user: IntercomUser | null;
  enabled?: boolean;
}

interface UseIntercomResult {
  isLoading: boolean;
  isInitialized: boolean;
  error: Error | null;
}

export function useIntercom(options: UseIntercomOptions): UseIntercomResult {
  const { user, enabled = true } = options;
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const initRef = useRef(false);

  useEffect(() => {
    if (!enabled || !user?.email || !user.instanceId || initRef.current) {
      setIsLoading(false);
      return;
    }

    const initIntercom = async (): Promise<void> => {
      try {
        setIsLoading(true);
        const config = await configService.getIntercomConfig();

        if (!config.appId) {
          setIsLoading(false);
          return;
        }

        const userId = `${user.instanceId}-${user.email}`.toLowerCase();

        let userHash: string | undefined;
        try {
          userHash = await intercomService.fetchUserSignature();
        } catch {
          // Identity verification is optional — proceed without it if the backend is unavailable
        }

        Intercom({
          app_id: config.appId,
          user_id: userId,
          email: user.email,
          name: user.name,
          user_hash: userHash,
          vertical_padding: config.verticalPadding,
          messenger_style_id: config.messengerStyleId,
        });

        initRef.current = true;
        setIsInitialized(true);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to initialize Intercom'));
      } finally {
        setIsLoading(false);
      }
    };

    initIntercom();

    const handleBeforeUnload = (): void => {
      shutdown();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (initRef.current) {
        shutdown();
        initRef.current = false;
      }
    };
  }, [user, enabled]);

  return { isLoading, isInitialized, error };
}
